import type { ReactNode } from "react";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { MetricLabel } from "./metric-label";
import { ACTIVE_TEXT, useActiveRow } from "./statement-item-action";
import { StatementItemActionButton } from "./statement-item-action-button";
import { WIDE_VALUE_LENGTH } from "./statement-layout";
import type { Metric } from "./statement-schema";

const ValueWithMark = ({
  value,
  active,
  children,
}: {
  value: string;
  active?: boolean;
  children: ReactNode;
}) => {
  const lastSpace = value.lastIndexOf(" ");
  return (
    <span className={cn({ [ACTIVE_TEXT]: active })}>
      {lastSpace === -1 ? null : value.slice(0, lastSpace + 1)}
      <span className="whitespace-nowrap">
        {lastSpace === -1 ? value : value.slice(lastSpace + 1)}
        {children}
      </span>
    </span>
  );
};

export const Field = ({
  metric,
  strong,
  prominent,
  muted,
  heading,
}: {
  metric: Metric;
  strong?: boolean;
  prominent?: boolean;
  muted?: boolean;
  heading?: string;
}) => {
  const active = useActiveRow({
    heading,
    line: metric.label ?? undefined,
    amount: metric.value,
  });

  return (
    <Stack
      align="start"
      gap={0}
      className={cn("min-w-0 gap-0.5", {
        "col-span-2": metric.value.length > WIDE_VALUE_LENGTH,
      })}
    >
      <MetricLabel>{metric.label}</MetricLabel>
      {/* The action sits inline after the value, not the label: these labels are
          long enough to wrap in a two-column grid on a phone, and values are not.
          It also matches the line-item and key/value rows, where the action always
          follows the amount. */}
      {prominent ? (
        <Text
          variant="label"
          className={cn("wrap-break-word", { "text-subtle": muted })}
        >
          <ValueWithMark value={metric.value} active={active}>
            <StatementItemActionButton
              heading={heading}
              line={metric.label ?? undefined}
              amount={metric.value}
            />
          </ValueWithMark>
        </Text>
      ) : (
        <p
          className={cn("text-sm wrap-break-word text-subtle", {
            "font-semibold text-ink": strong,
          })}
        >
          <ValueWithMark value={metric.value} active={active}>
            <StatementItemActionButton
              heading={heading}
              line={metric.label ?? undefined}
              amount={metric.value}
            />
          </ValueWithMark>
        </p>
      )}
    </Stack>
  );
};
